import axios, { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { API_END_POINT } from './api';

export type PublicWorkspaceCustomerReservation = {
  reservationKey: string;
  date: string;
  time: string;
  shopName: string;
  courseName: string;
  status: 'available' | 'notAllowedCustomerChange' | 'expired' | 'canceled';
  shopUid: string;
  courseUid: string;
  canCancelReservationFromPublic: boolean;
};

type ResponseBody = {
  reservations: Array<{
    reservationKey: string;
    date: string;
    startHour: number;
    startMinute: number;
    courseName: string;
    courseUid: string;
    shopName: string;
    shopUid: string;
    status: string;
    canCancelReservationFromPublic: boolean;
  }>;
};

export const useWorkspaceCustomerReservations = (
  workspaceUid: string,
  lineSessionKey?: string
) => {
  const [reservations, setReservations] = useState<
    PublicWorkspaceCustomerReservation[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!workspaceUid || !lineSessionKey) {
      return;
    }

    const fetchReservations = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const url = `${API_END_POINT}/public/ws/${workspaceUid}/customer-reservations?lineSessionKey=${lineSessionKey}`;
        const response: AxiosResponse<ResponseBody> = await axios.get(url);

        const formattedReservations: PublicWorkspaceCustomerReservation[] =
          response.data.reservations.map((reservation) => {
            return {
              reservationKey: reservation.reservationKey,
              date: reservation.date,
              time: `${String(reservation.startHour).padStart(2, '0')}:${String(
                reservation.startMinute
              ).padStart(2, '0')}`,
              shopName: reservation.shopName,
              courseName: reservation.courseName,
              status: reservation.status as
                | 'available'
                | 'notAllowedCustomerChange'
                | 'expired'
                | 'canceled',
              shopUid: reservation.shopUid,
              courseUid: reservation.courseUid,
              canCancelReservationFromPublic:
                reservation.canCancelReservationFromPublic,
            };
          });

        setReservations(formattedReservations);
      } catch (err) {
        setError(err as Error);
        console.error('Failed to fetch workspace customer reservations:', err);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchReservations();
  }, [workspaceUid, lineSessionKey]);

  return {
    reservations,
    isLoadingReservations: isLoading,
    error,
  };
};
